import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SalesChannelList from '../components/SalesChannelList';

const ChannelsPage = ({
  user,
  channels,
  isLoadingChannels,
  showChannelForm,
  setShowChannelForm,
  channelFormData,
  setChannelFormData,
  editingChannel,
  setEditingChannel,
  fetchChannels,
  handleChannelInputChange,
  handleChannelSubmit,
  startEditingChannel,
  cancelChannelEdit,
  deleteChannel,
  isSubmitting,
  needsInitialChannel,
  showSuccessMessage,
  setError,
  integrationStatuses,
  platforms,
  isLoadingStatuses,
  isConnecting,
  isDisconnecting,
  isSyncing,
  isImporting,
  isPulling,
  lastSyncResult,
  lastImportResult,
  lastPullResult,
  conflicts,
  isLoadingConflicts,
  isResolvingConflict,
  fetchPlatforms,
  fetchIntegrationStatuses,
  connectChannel,
  disconnectChannel,
  syncInventory,
  importSales,
  pullInventory,
  fetchConflicts,
  resolveConflict,
  resolveAllConflicts,
  clearResults,
  quotaUsage,
  fetchQuotaUsage,
  quailhqStatuses,
  isLoadingQuailHQStatus,
  fetchQuailHQStatus,
  isRequestingBarcodes,
  requestBarcodes,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchChannels();
    }
  }, [user, fetchChannels]);

  // Fetch integration statuses when channels are loaded
  useEffect(() => {
    if (channels && channels.length > 0) {
      const channelIds = channels.map(ch => ch.id);
      fetchIntegrationStatuses(channelIds);
    }
  }, [channels, fetchIntegrationStatuses]);

  // Fetch QuailHQ statuses when channels are loaded
  useEffect(() => {
    if (channels && channels.length > 0 && fetchQuailHQStatus) {
      const channelIds = channels.map(ch => ch.id);
      fetchQuailHQStatus(channelIds);
    }
  }, [channels, fetchQuailHQStatus]);

  // Handle OAuth callback redirect params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const channelId = searchParams.get('channelId');
    const error = searchParams.get('error');

    if (connected === null) return;

    if (connected === 'true' && channelId) {
      showSuccessMessage('Successfully connected to platform!');
      fetchIntegrationStatuses([channelId]);
      fetchQuotaUsage?.();
    } else if (connected === 'false') {
      setError(error ? decodeURIComponent(error) : 'Failed to connect to platform');
    }

    // Clear URL params to prevent re-triggering
    navigate('/channels', { replace: true });
  }, [searchParams, navigate, showSuccessMessage, setError, fetchIntegrationStatuses, fetchQuotaUsage]);

  useEffect(() => {
    if (!needsInitialChannel) {
      return;
    }
    if (!showChannelForm) {
      setEditingChannel(null);
      setChannelFormData({ name: '', defaultLocation: '', useItemNumber: false, useCustomItemNumber: false, itemNumberPrefix: '', itemNumberStart: '', labelRequired: false, barcodeRequired: false, barcodeFormat: '', barcodeSource: 'itemNumber', quailhqEnabled: false, quailhqAccountId: '' });
      setShowChannelForm(true);
    }
  }, [needsInitialChannel, showChannelForm, setEditingChannel, setChannelFormData, setShowChannelForm]);

  return (
    <>
      <SalesChannelList
        channels={channels}
        isLoading={isLoadingChannels}
        onEdit={startEditingChannel}
        onDelete={deleteChannel}
        onAddChannel={() => {
          setEditingChannel(null);
          setChannelFormData({ name: '', defaultLocation: '', useItemNumber: false, useCustomItemNumber: false, itemNumberPrefix: '', itemNumberStart: '', labelRequired: false, barcodeRequired: false, barcodeFormat: '', barcodeSource: 'itemNumber', quailhqEnabled: false, quailhqAccountId: '' });
          setShowChannelForm(true);
        }}
        channelFormData={channelFormData}
        onInputChange={handleChannelInputChange}
        onSubmit={handleChannelSubmit}
        isSubmitting={isSubmitting}
        onCancelEdit={cancelChannelEdit}
        isEditing={!!editingChannel}
        integrationStatuses={integrationStatuses}
        platforms={platforms}
        isLoadingStatuses={isLoadingStatuses}
        isConnecting={isConnecting}
        isDisconnecting={isDisconnecting}
        isSyncing={isSyncing}
        isImporting={isImporting}
        isPulling={isPulling}
        lastSyncResult={lastSyncResult}
        lastImportResult={lastImportResult}
        lastPullResult={lastPullResult}
        conflicts={conflicts}
        isLoadingConflicts={isLoadingConflicts}
        isResolvingConflict={isResolvingConflict}
        onFetchPlatforms={fetchPlatforms}
        onConnect={connectChannel}
        onDisconnect={disconnectChannel}
        onReconnect={(channelId) => {
          const status = integrationStatuses[channelId];
          if (status?.platform) {
            connectChannel(channelId, status.platform);
          }
        }}
        onSyncInventory={syncInventory}
        onImportSales={importSales}
        onPullInventory={pullInventory}
        onFetchConflicts={fetchConflicts}
        onResolveConflict={resolveConflict}
        onResolveAllConflicts={resolveAllConflicts}
        onClearResults={clearResults}
        quotaUsage={quotaUsage}
        fetchQuotaUsage={fetchQuotaUsage}
        quailhqStatuses={quailhqStatuses}
      />
    </>
  );
};

export default ChannelsPage;
